import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment";

import LanguageContext from "@/context/language/LanguageContext";

import { useSharedRangePicker } from "../../shared/hooks/useSharedRangePicker";
import { PICKING_STATE, SLIDE_DIRECTION, VIEW_MODE } from "../../shared/types";
import { MonthRangePickerProps } from "../types";

export const useMonthRangePicker = ({
  disabled,
  externalValue,
  onChange
}: Pick<MonthRangePickerProps, "disabled" | "externalValue" | "onChange">) => {
  const { language } = useContext(LanguageContext);

  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const [startMonth, setStartMonth] = useState<number | null>(null);
  const [endMonth, setEndMonth] = useState<number | null>(null);
  const [hoverMonth, setHoverMonth] = useState<number | null>(null);
  const [picking, setPicking] = useState<PICKING_STATE | null>(null);
  const [open, setOpen] = useState(false);

  const months = moment.localeData(language).monthsShort();

  const {
    year,
    setYear,
    viewMode,
    setViewMode,
    viewVisible,
    slideClass,
    cancelHoverClear,
    animateSlide,
    animateFadeView
  } = useSharedRangePicker({ initialYear: currentYear });

  const toMonthKey = (yearValue: number, monthIndex: number) =>
    yearValue * 12 + monthIndex;

  useEffect(() => {
    if (open) return;

    if (!externalValue) {
      return;
    }

    const { start, end } = externalValue;
    setStartMonth(toMonthKey(start.getFullYear(), start.getMonth()));
    setEndMonth(toMonthKey(end.getFullYear(), end.getMonth()));
  }, [externalValue, open]);

  const noStartMonth = startMonth === null;
  const noEndMonth = endMonth === null;

  const openPicker = () => {
    if (disabled) return;
    setViewMode(VIEW_MODE.Month);
    setOpen(true);
    setPicking(PICKING_STATE.Start);
    if (noStartMonth && noEndMonth) {
      setYear(currentYear);
    }
  };

  const closePicker = () => {
    setOpen(false);
    setPicking(null);
    setHoverMonth(null);
    cancelHoverClear();
  };

  const handleMonthClick = (clickedKey: number) => {
    if (picking === PICKING_STATE.Start) {
      setStartMonth(clickedKey);
      setEndMonth(null);
      setHoverMonth(null);
      setPicking(PICKING_STATE.End);
      onChange?.(null);
      return;
    }

    if (picking === PICKING_STATE.End && startMonth !== null) {
      if (clickedKey < startMonth) {
        setStartMonth(clickedKey);
        setEndMonth(null);
        setHoverMonth(null);
        setPicking(PICKING_STATE.End);
        onChange?.(null);
        return;
      }

      setEndMonth(clickedKey);
      setHoverMonth(null);
      setPicking(null);
      cancelHoverClear();

      onChange?.({
        start: new Date(Math.floor(startMonth / 12), startMonth % 12, 1),
        end: new Date(Math.floor(clickedKey / 12), (clickedKey % 12) + 1, 0)
      });

      closePicker();
    }
  };

  const handleNavigateBack = () => {
    animateSlide(SLIDE_DIRECTION.Right, () => setYear(prev => prev - 1));
  };

  const handleNavigateForward = () => {
    animateSlide(SLIDE_DIRECTION.Left, () => setYear(prev => prev + 1));
  };

  const handleHeaderClick = () => {
    const nextMode =
      viewMode === VIEW_MODE.Month ? VIEW_MODE.Year : VIEW_MODE.Month;
    animateFadeView(() => setViewMode(nextMode));
  };

  const handleYearSelect = (selectedYear: number) => {
    animateFadeView(() => {
      setYear(selectedYear);
      setViewMode(VIEW_MODE.Month);
    });
  };

  const handleClear = (event: React.MouseEvent) => {
    if (disabled) return;
    event.stopPropagation();
    cancelHoverClear();
    onChange?.(null);
    setStartMonth(null);
    setEndMonth(null);
    setHoverMonth(null);
    setPicking(PICKING_STATE.Start);
  };

  const getDisplayValue = () => {
    if (noStartMonth) return t("Filter##Select date range");

    const formatKey = (key: number) =>
      `${months[key % 12]} ${Math.floor(key / 12)}`;

    const start = Math.min(startMonth, endMonth ?? startMonth);
    const end = noEndMonth ? null : Math.max(startMonth, endMonth);

    return t("Filter##from to date", {
      fromDate: formatKey(start),
      toDate: end === null ? "" : formatKey(end)
    });
  };

  return {
    months,
    year,
    open,
    viewMode,
    viewVisible,
    slideClass,
    startMonth,
    endMonth,
    hoverMonth,
    picking,
    noStartMonth,
    noEndMonth,
    hasClearableValue: startMonth !== null || endMonth !== null,
    setHoverMonth,
    openPicker,
    closePicker,
    handleMonthClick,
    handleNavigateBack,
    handleNavigateForward,
    handleHeaderClick,
    handleYearSelect,
    handleClear,
    cancelHoverClear,
    getDisplayValue
  };
};
